import type {
	API_CalendarColorLegendUpdateRequest,
	API_CalendarEnrollmentStatusFilterUpdateRequest,
	API_CalendarSessionTitleUpdateRequest,
	API_CreateOrganizationRequest,
	API_CreateOrganizationResponse,
	API_DeleteOrganizationResponse,
	API_EnrollmentScheduleSelectionUpdateRequest,
	API_GetOrganizationFeaturesResponse,
	API_ListUserOrganizationsResponse,
	API_ScheduleOverlapValidationUpdateRequest,
	FeatFlagCalendarSessionColorLegend,
	FeatFlagCalendarSessionEnrollmentStatusFilter,
} from "@pilatestime/shared-types";
import {
	DEFAULT_CALENDAR_SESSION_COLOR_LEGEND,
	DEFAULT_CALENDAR_SESSION_ENROLLMENT_STATUS_FILTER,
	FeatFlagCalendarSessionTitleViewMode,
	FeatFlagEnrollmentScheduleSelectionMode,
	FeatFlagScheduleOverlapValidation,
} from "@pilatestime/shared-types";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import { scheduleSessionKeys } from "@/hooks/useScheduleSessions";
import api from "./api_client";

// API functions
export const fetchUserOrganizations = (
	token: string,
): Promise<API_ListUserOrganizationsResponse> =>
	api
		.get("/organizations/me", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const createOrganization = (
	token: string,
	data: API_CreateOrganizationRequest,
): Promise<API_CreateOrganizationResponse> =>
	api
		.post("/organizations", data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateOrganization = (
	token: string,
	organizationId: string,
	data: Partial<API_CreateOrganizationRequest>,
): Promise<API_CreateOrganizationResponse> =>
	api
		.put(`/organizations/${organizationId}`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const leaveOrganization = (
	token: string,
	organizationId: string,
): Promise<{ success: boolean; message: string }> =>
	api
		.delete(`/organizations/${organizationId}/leave`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const deleteOrganization = (
	token: string,
	organizationId: string,
): Promise<API_DeleteOrganizationResponse> =>
	api
		.delete(`/organizations/${organizationId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

// Individual feature flag API functions
const featureBasePath = (orgId: string) => `/organizations/${orgId}/features`;

export const fetchEnrollmentScheduleSelection = (
	token: string,
	organizationId: string,
): Promise<{ value: FeatFlagEnrollmentScheduleSelectionMode }> =>
	api
		.get(`${featureBasePath(organizationId)}/enrollment/schedule-selection`, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const updateEnrollmentScheduleSelection = (
	token: string,
	organizationId: string,
	data: API_EnrollmentScheduleSelectionUpdateRequest,
): Promise<{ value: FeatFlagEnrollmentScheduleSelectionMode }> =>
	api
		.put(
			`${featureBasePath(organizationId)}/enrollment/schedule-selection`,
			data,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

export const fetchCalendarSessionTitle = (
	token: string,
	organizationId: string,
): Promise<{ value: FeatFlagCalendarSessionTitleViewMode }> =>
	api
		.get(
			`${featureBasePath(organizationId)}/attendance/calendar/session-title`,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

export const updateCalendarSessionTitle = (
	token: string,
	organizationId: string,
	data: API_CalendarSessionTitleUpdateRequest,
): Promise<{ value: FeatFlagCalendarSessionTitleViewMode }> =>
	api
		.put(
			`${featureBasePath(organizationId)}/attendance/calendar/session-title`,
			data,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

export const fetchCalendarColorLegend = (
	token: string,
	organizationId: string,
): Promise<{ value: FeatFlagCalendarSessionColorLegend }> =>
	api
		.get(
			`${featureBasePath(organizationId)}/attendance/calendar/color-legend`,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

export const updateCalendarColorLegend = (
	token: string,
	organizationId: string,
	data: API_CalendarColorLegendUpdateRequest,
): Promise<{ value: FeatFlagCalendarSessionColorLegend }> =>
	api
		.put(
			`${featureBasePath(organizationId)}/attendance/calendar/color-legend`,
			data,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

export const fetchCalendarEnrollmentStatusFilter = (
	token: string,
	organizationId: string,
): Promise<{ value: FeatFlagCalendarSessionEnrollmentStatusFilter }> =>
	api
		.get(
			`${featureBasePath(organizationId)}/attendance/calendar/enrollment-status-filter`,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

export const updateCalendarEnrollmentStatusFilter = (
	token: string,
	organizationId: string,
	data: API_CalendarEnrollmentStatusFilterUpdateRequest,
): Promise<{ value: FeatFlagCalendarSessionEnrollmentStatusFilter }> =>
	api
		.put(
			`${featureBasePath(organizationId)}/attendance/calendar/enrollment-status-filter`,
			data,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

export const fetchScheduleOverlapValidation = (
	token: string,
	organizationId: string,
): Promise<{ value: FeatFlagScheduleOverlapValidation }> =>
	api
		.get(`${featureBasePath(organizationId)}/schedule/overlap-validation`, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const updateScheduleOverlapValidation = (
	token: string,
	organizationId: string,
	data: API_ScheduleOverlapValidationUpdateRequest,
): Promise<{ value: FeatFlagScheduleOverlapValidation }> =>
	api
		.put(
			`${featureBasePath(organizationId)}/schedule/overlap-validation`,
			data,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

// Aggregated fetch for all features (calls individual endpoints in parallel)
export const fetchOrganizationFeatures = async (
	token: string,
	organizationId: string,
): Promise<API_GetOrganizationFeaturesResponse> => {
	const [
		enrollmentScheduleSelection,
		calendarSessionTitle,
		calendarColorLegend,
		calendarEnrollmentStatusFilter,
		scheduleOverlapValidation,
	] = await Promise.all([
		fetchEnrollmentScheduleSelection(token, organizationId),
		fetchCalendarSessionTitle(token, organizationId),
		fetchCalendarColorLegend(token, organizationId),
		fetchCalendarEnrollmentStatusFilter(token, organizationId),
		fetchScheduleOverlapValidation(token, organizationId),
	]);

	return {
		enrollment: {
			scheduleSelection: { mode: enrollmentScheduleSelection.value },
		},
		attendance: {
			calendar: {
				sessionTitleViewMode: calendarSessionTitle.value,
				colorLegend: calendarColorLegend.value,
				enrollmentStatusFilter: calendarEnrollmentStatusFilter.value,
			},
		},
		schedule: {
			overlapValidation: scheduleOverlapValidation.value,
		},
	};
};

// Query options
export const fetchUserOrganizationsQueryOptions = (
	userId: string,
	token: string,
) =>
	queryOptions({
		queryKey: ["user_organizations", userId],
		queryFn: () => fetchUserOrganizations(token),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchOrganizationFeaturesQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["organization_features", organizationId, userId],
		queryFn: () => fetchOrganizationFeatures(token, organizationId),
		staleTime: 10 * 60 * 1000, // 10 minutes (features change infrequently)
	});

// Mutation hooks
export const useCreateOrganization = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			data,
		}: {
			token: string;
			data: API_CreateOrganizationRequest;
		}) => createOrganization(token, data),
		toastMessages: {
			loading: t("organization.toasts.creating"),
			success: t("organization.toasts.createSuccess"),
			error: t("organization.toasts.createError"),
		},
		onSuccess: () => {
			// Invalidate and refetch user organizations
			queryClient.invalidateQueries({
				queryKey: ["user_organizations"],
			});

			// Also invalidate user permissions since they might have changed
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};

export const useUpdateOrganization = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: Partial<API_CreateOrganizationRequest>;
		}) => updateOrganization(token, organizationId, data),
		toastMessages: {
			loading: t("organization.toasts.updating"),
			success: t("organization.toasts.updateSuccess"),
			error: t("organization.toasts.updateError"),
		},
		onSuccess: () => {
			// Invalidate and refetch user organizations
			queryClient.invalidateQueries({
				queryKey: ["user_organizations"],
			});

			// Also invalidate user permissions since organization name might have changed
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};

export const useLeaveOrganization = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
		}: {
			token: string;
			organizationId: string;
		}) => leaveOrganization(token, organizationId),
		toastMessages: {
			loading: t("organization.toasts.leaving"),
			success: t("organization.toasts.leaveSuccess"),
			error: t("organization.toasts.leaveError"),
		},
		onSuccess: () => {
			// Invalidate and refetch user organizations
			queryClient.invalidateQueries({
				queryKey: ["user_organizations"],
			});

			// Also invalidate user permissions since user left organization
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};

export const useDeleteOrganization = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
		}: {
			token: string;
			organizationId: string;
		}) => deleteOrganization(token, organizationId),
		toastMessages: {
			loading: t("organization.toasts.deleting"),
			success: t("organization.toasts.deleteSuccess"),
			error: t("organization.toasts.deleteError"),
		},
		onSuccess: () => {
			// Invalidate and refetch user organizations
			queryClient.invalidateQueries({
				queryKey: ["user_organizations"],
			});

			// Also invalidate user permissions since organization set changed
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};

// Custom hook for organization features
export const useOrgFeatures = (
	userId: string,
	token: string,
	organizationId: string,
) => {
	const query = useQuery({
		...fetchOrganizationFeaturesQueryOptions(userId, token, organizationId),
		// Provide fallback data if API fails
		placeholderData: {
			enrollment: {
				scheduleSelection: {
					mode: FeatFlagEnrollmentScheduleSelectionMode.EXISTING_ONLY,
				},
			},
			attendance: {
				calendar: {
					sessionTitleViewMode:
						FeatFlagCalendarSessionTitleViewMode.SCHEDULE_NAME,
					colorLegend: DEFAULT_CALENDAR_SESSION_COLOR_LEGEND,
					enrollmentStatusFilter:
						DEFAULT_CALENDAR_SESSION_ENROLLMENT_STATUS_FILTER,
				},
			},
			schedule: {
				overlapValidation: FeatFlagScheduleOverlapValidation.BLOCK,
			},
		},
	});

	return {
		...query,
		// Extract the enrollment schedule selection mode for convenience
		enrollmentScheduleSelectionMode:
			query.data?.enrollment.scheduleSelection.mode ||
			FeatFlagEnrollmentScheduleSelectionMode.EXISTING_ONLY,
		// Extract the calendar session title view mode for convenience
		calendarSessionTitleViewMode:
			query.data?.attendance?.calendar?.sessionTitleViewMode ||
			FeatFlagCalendarSessionTitleViewMode.SCHEDULE_NAME,
		// Extract the color legend for convenience
		calendarSessionColorLegend:
			query.data?.attendance?.calendar?.colorLegend ||
			DEFAULT_CALENDAR_SESSION_COLOR_LEGEND,
		// Extract the enrollment status filter for convenience
		calendarSessionEnrollmentStatusFilter:
			query.data?.attendance?.calendar?.enrollmentStatusFilter ||
			DEFAULT_CALENDAR_SESSION_ENROLLMENT_STATUS_FILTER,
		// Extract the schedule overlap validation for convenience
		scheduleOverlapValidation:
			query.data?.schedule?.overlapValidation ||
			FeatFlagScheduleOverlapValidation.BLOCK,
	};
};

// Individual mutation hooks for each feature flag
export const useUpdateEnrollmentScheduleSelection = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			value,
		}: {
			token: string;
			organizationId: string;
			value: FeatFlagEnrollmentScheduleSelectionMode;
		}) => updateEnrollmentScheduleSelection(token, organizationId, { value }),
		toastMessages: {
			loading: t("orgSettings.features.toasts.updating"),
			success: t("orgSettings.features.toasts.updateSuccess"),
			error: t("orgSettings.features.toasts.updateError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["organization_features", variables.organizationId],
			});
		},
	});
};

export const useUpdateCalendarSessionTitle = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			value,
		}: {
			token: string;
			organizationId: string;
			value: FeatFlagCalendarSessionTitleViewMode;
		}) => updateCalendarSessionTitle(token, organizationId, { value }),
		toastMessages: {
			loading: t("orgSettings.features.toasts.updating"),
			success: t("orgSettings.features.toasts.updateSuccess"),
			error: t("orgSettings.features.toasts.updateError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["organization_features", variables.organizationId],
			});
		},
	});
};

export const useUpdateCalendarColorLegend = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			value,
		}: {
			token: string;
			organizationId: string;
			value: Partial<FeatFlagCalendarSessionColorLegend>;
		}) => updateCalendarColorLegend(token, organizationId, { value }),
		toastMessages: {
			loading: t("orgSettings.features.toasts.updating"),
			success: t("orgSettings.features.toasts.updateSuccess"),
			error: t("orgSettings.features.toasts.updateError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["organization_features", variables.organizationId],
			});
			// Invalidate schedule sessions to refetch with new color settings
			queryClient.invalidateQueries({
				queryKey: scheduleSessionKeys.all,
			});
		},
	});
};

export const useUpdateCalendarEnrollmentStatusFilter = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			value,
		}: {
			token: string;
			organizationId: string;
			value: Partial<FeatFlagCalendarSessionEnrollmentStatusFilter>;
		}) =>
			updateCalendarEnrollmentStatusFilter(token, organizationId, { value }),
		toastMessages: {
			loading: t("orgSettings.features.toasts.updating"),
			success: t("orgSettings.features.toasts.updateSuccess"),
			error: t("orgSettings.features.toasts.updateError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["organization_features", variables.organizationId],
			});
			// Invalidate schedule sessions to refetch with new enrollment status filter
			queryClient.invalidateQueries({
				queryKey: scheduleSessionKeys.all,
			});
		},
	});
};

export const useUpdateScheduleOverlapValidation = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			value,
		}: {
			token: string;
			organizationId: string;
			value: FeatFlagScheduleOverlapValidation;
		}) => updateScheduleOverlapValidation(token, organizationId, { value }),
		toastMessages: {
			loading: t("orgSettings.features.toasts.updating"),
			success: t("orgSettings.features.toasts.updateSuccess"),
			error: t("orgSettings.features.toasts.updateError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["organization_features", variables.organizationId],
			});
		},
	});
};
